import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import MailIcon from "@mui/icons-material/Mail";
import DraftsIcon from "@mui/icons-material/Drafts";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const ContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    replied: 0,
    pending: 0,
  });
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const baseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    fetchMessages();
    fetchStats();
  }, [baseUrl]);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${baseUrl}/auth/contact/messages`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setMessages(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching messages:", error);
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${baseUrl}/auth/contact/messages/stats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleViewMessage = async (message) => {
    setSelectedMessage(message);
    setAdminNotes(message.adminNotes || "");
    setOpenDialog(true);

    // Mark as read if not already
    if (!message.isRead) {
      try {
        const token = localStorage.getItem("token");
        await axios.put(
          `${baseUrl}/auth/contact/messages/${message.id}`,
          {
            isRead: true,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        fetchMessages();
        fetchStats();
      } catch (error) {
        console.error("Error marking message as read:", error);
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedMessage(null);
    setAdminNotes("");
  };

  const handleToggleReplied = async () => {
    if (!selectedMessage) return;

    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${baseUrl}/auth/contact/messages/${selectedMessage.id}`,
        {
          isReplied: !selectedMessage.isReplied,
          adminNotes: adminNotes,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      alert("Message updated successfully");
      fetchMessages();
      fetchStats();
      handleCloseDialog();
    } catch (error) {
      console.error("Error updating message:", error);
      alert("Failed to update message");
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedMessage) return;

    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${baseUrl}/auth/contact/messages/${selectedMessage.id}`,
        {
          adminNotes: adminNotes,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      alert("Notes saved successfully");
      fetchMessages();
      setSelectedMessage({ ...selectedMessage, adminNotes });
    } catch (error) {
      console.error("Error saving notes:", error);
      alert("Failed to save notes");
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this message?",
    );
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${baseUrl}/auth/contact/messages/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setMessages((prevMessages) =>
        prevMessages.filter((msg) => msg.id !== id),
      );
      fetchStats();
      alert("Message deleted successfully");
    } catch (error) {
      console.error("Error deleting message:", error);
      alert("Failed to delete message");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3, mt: 3 }}>
          Contact Messages
        </Typography>
        <Typography>Loading messages...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mb: 4 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3, mt: 3 }}>
        Contact Messages
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Messages
              </Typography>
              <Typography variant="h4">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Unread
              </Typography>
              <Typography variant="h4" color="error">
                {stats.unread}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Replied
              </Typography>
              <Typography variant="h4" color="success">
                {stats.replied}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper} style={{ overflowX: "auto" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {messages.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  sx={{ color: "#000000", textAlign: "center" }}
                >
                  No messages found
                </TableCell>
              </TableRow>
            ) : (
              messages.map((message) => (
                <TableRow
                  key={message.id}
                  sx={{
                    backgroundColor: message.isRead ? "transparent" : "#f3f4f6",
                  }}
                >
                  <TableCell
                    sx={{ color: "#000000", fontSize: "1rem", fontWeight: 500 }}
                  >
                    {message.name}
                  </TableCell>
                  <TableCell sx={{ color: "#000000", fontSize: "0.9rem" }}>
                    {message.email}
                  </TableCell>
                  <TableCell sx={{ color: "#000000", fontSize: "0.9rem" }}>
                    {message.subject}
                  </TableCell>
                  <TableCell sx={{ color: "#000000", fontSize: "0.9rem" }}>
                    {formatDate(message.createdAt)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {!message.isRead && (
                        <Chip
                          icon={<MailIcon />}
                          label="Unread"
                          color="error"
                          size="small"
                        />
                      )}
                      {message.isRead && !message.isReplied && (
                        <Chip
                          icon={<DraftsIcon />}
                          label="Read"
                          color="default"
                          size="small"
                        />
                      )}
                      {message.isReplied && (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Replied"
                          color="success"
                          size="small"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton
                        color="info"
                        onClick={() => handleViewMessage(message)}
                        title="View Message"
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(message.id)}
                        title="Delete Message"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Message Detail Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Message Details</DialogTitle>
        <DialogContent>
          {selectedMessage && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Name:
                  </Typography>
                  <Typography variant="body1">{selectedMessage.name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Email:
                  </Typography>
                  <Typography variant="body1">
                    {selectedMessage.email}
                  </Typography>
                </Grid>
                {selectedMessage.phone && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Phone:
                    </Typography>
                    <Typography variant="body1">
                      {selectedMessage.phone}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Date:
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedMessage.createdAt)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Subject:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedMessage.subject}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Message:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      mt: 1,
                      p: 2,
                      backgroundColor: "#f9fafb",
                      borderRadius: 1,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {selectedMessage.message}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Admin Notes"
                    multiline
                    rows={4}
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add internal notes about this message..."
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSaveNotes} color="info">
            Save Notes
          </Button>
          <Button onClick={handleToggleReplied} color="primary">
            {selectedMessage?.isReplied
              ? "Mark as Not Replied"
              : "Mark as Replied"}
          </Button>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ContactMessages;
